import { useState, forwardRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AttendanceIndicator, HomeworkIndicator, HomeworkAssignedIndicator, VideoIndicator, Tooltip } from './StatusIndicator';
import ModuleDropdown from './ModuleDropdown';
import { formatDate, DATE_FORMATS } from '../../utils/dateFormatter';

const ModuleCard = forwardRef(function ModuleCard({ module, isToday }, ref) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const formatModuleDate = (dateString) => {
    if (!dateString) return '';
    return formatDate(dateString, { ...DATE_FORMATS.shortDayMonth, year: 'numeric' });
  };

  // Translate "Class Module X" to localized version
  const getModuleTitle = (title) => {
    if (!title) return '';
    if (title.startsWith('Class Module')) {
      return title.replace('Class Module', t('module.classModule'));
    }
    return title;
  };

  const isFutureModule = () => {
    if (!module.date) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const moduleDate = new Date(module.date);
    const moduleDateOnly = new Date(moduleDate.getFullYear(), moduleDate.getMonth(), moduleDate.getDate());
    return moduleDateOnly > today;
  };

  const hasIndicators =
    (!module.is_canceled && module.attendance_status != null) ||
    module.homework_status != null ||
    module.video_watched != null;

  return (
    <div
      ref={ref}
      className={`bg-white dark:bg-gray-800 rounded-xl mb-3 sm:mb-4 shadow-sm hover:shadow-md dark:shadow-gray-900/30 transition-all duration-200 overflow-hidden card-interactive ${
        isToday ? 'ring-2 ring-[#589DD5] ring-offset-2 dark:ring-offset-gray-900' : ''
      }`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-3 sm:p-5"
      >
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
            <div className={`shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors ${
              isToday ? 'bg-[#589DD5] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}>
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
                  {getModuleTitle(module.title)}
                </h3>
                {isToday && (
                  <span className="flex-shrink-0 px-1.5 py-0.5 sm:px-2 bg-[#589DD5] text-white text-[10px] sm:text-xs font-medium rounded-full">
                    {t('module.today')}
                  </span>
                )}
                {module.is_canceled > 0 && (
                  <Tooltip
                    text={
                      Number(module.is_canceled) === 1
                        ? t('module.canceledByStudentDesc')
                        : Number(module.is_canceled) === 2
                          ? t('module.canceledByTeacherDesc')
                          : t('module.canceledByOfficeDesc')
                    }
                  >
                    <span className="flex-shrink-0 px-1.5 py-0.5 sm:px-2 bg-slate-100 dark:bg-transparent text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-500 text-[10px] sm:text-xs font-medium rounded-full">
                      {Number(module.is_canceled) === 1
                        ? t('module.canceledByStudent')
                        : Number(module.is_canceled) === 2
                          ? t('module.canceledByTeacher')
                          : t('module.canceledByOffice')}
                    </span>
                  </Tooltip>
                )}
                {module.is_makeup && (
                  <Tooltip text={t('module.makeupDesc')}>
                    <span className="flex-shrink-0 px-1.5 py-0.5 sm:px-2 bg-amber-50 dark:bg-transparent text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-600 text-[10px] sm:text-xs font-medium rounded-full">
                      {t('module.makeup')}
                    </span>
                  </Tooltip>
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {formatModuleDate(module.date)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Icon-only indicators when collapsed */}
            {hasIndicators && !isOpen && (
              <div className="flex items-center gap-1">
                {!module.is_canceled && <AttendanceIndicator status={module.attendance_status} iconOnly />}
                <HomeworkAssignedIndicator hasHomework={module.homework_status != null} iconOnly />
                <HomeworkIndicator status={module.homework_status} iconOnly />
                <VideoIndicator watched={module.video_watched} iconOnly />
              </div>
            )}
            <div className={`shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
              isOpen ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}>
              <ChevronDown
                className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>
        </div>
      </button>

      <ModuleDropdown
        eventId={module.id}
        eventDate={module.date}
        isOpen={isOpen}
        isFuture={isFutureModule()}
        attendanceStatus={module.attendance_status}
        homeworkStatus={module.homework_status}
        videoWatched={module.video_watched}
        isCanceled={module.is_canceled}
      />
    </div>
  );
});

export default ModuleCard;
